import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Frozen analytical showcase from temp_reposit/DTP_STATIC_HTML_REPORT_V2/06_frequency_spatial.html.
const COUNT_HOLDOUT = [
  { model: "P2_poisson_full_primary", nll: 0.228938, poissonDeviance: 0.316436 },
  { model: "NB2_negative_binomial_full_primary", nll: 0.225963, poissonDeviance: 0.316906 },
];

const BINARY_HOLDOUT = [
  { model: "L2_logistic_full_primary", rocAuc: 0.865866, prAuc: 0.311212, logLoss: 0.184326 },
  { model: "L3_logistic_plus_sensitivity", rocAuc: 0.873801, prAuc: 0.337256, logLoss: 0.179931 },
];

const TEMPORAL_VALIDATION = [
  { metric: "ROC AUC", logistic: 0.868501 },
  { metric: "PR AUC", logistic: 0.302282 },
  { metric: "Log loss", logistic: 0.172059 },
  { metric: "Model NLL", nb2: 0.210426 },
  { metric: "Poisson deviance", nb2: 0.298584 },
  { metric: "Spearman", nb2: 0.304166 },
];

const SPATIAL_VALIDATION = [
  { metric: "ROC AUC", logistic: 0.827421 },
  { metric: "PR AUC", logistic: 0.204157 },
  { metric: "Log loss", logistic: 0.144444 },
  { metric: "Model NLL", nb2: 0.175951 },
  { metric: "Poisson deviance", nb2: 0.255618 },
  { metric: "Spearman", nb2: 0.215323 },
];

const INFRASTRUCTURE_EFFECTS = [
  ["parking_presence_flag", 0.775356, 0.67138, 0.895434],
  ["tunnel_presence_flag", 0.786243, 0.702121, 0.880444],
  ["curve_count", 0.787599, 0.766326, 0.809463],
  ["incline_presence_flag", 0.862209, 0.751345, 0.989431],
  ["bridge_presence_flag", 0.864702, 0.795405, 0.940036],
  ["sidewalk_presence_flag", 0.868631, 0.79786, 0.945679],
  ["sharp_curve_presence_flag", 1.101305, 1.050777, 1.154263],
  ["perpendicular_intersection_presence_flag", 1.107663, 1.049329, 1.169239],
  ["ramp_presence_flag", 1.131589, 1.054423, 1.214403],
  ["crossing_count", 1.17315, 1.152423, 1.19425],
  ["sharp_intersection_presence_flag", 1.182196, 1.108501, 1.26079],
  ["oneway_pct", 1.18514, 1.145624, 1.226019],
  ["traffic_signal_count_presence_flag", 1.400574, 1.325426, 1.479983],
  ["lighting_pct", 1.850396, 1.77262, 1.931584],
  ["road_count", 1.924612, 1.847252, 2.005212],
].map(([label, ratio, ciLow, ciHigh]) => ({ label, ratio, ciLow, ciHigh }));

const MORAN = [
  { label: "accident_count_year", moran: 0.128608 },
  { label: "count_response_residual", moran: 0.055371 },
  { label: "count_pearson_residual", moran: 0.053815 },
  { label: "count_deviance_residual", moran: 0.061017 },
  { label: "binary_response_residual", moran: 0.051901 },
];

const RISK_CONCENTRATION = [
  { label: "Очень низкий", h3: 24.474966, accidents: 0.732477, fatal: 3.076923 },
  { label: "Низкий", h3: 15.529915, accidents: 1.171963, fatal: 1.538462 },
  { label: "Средний", h3: 19.98742, accidents: 5.025918, fatal: 5.538462 },
  { label: "Высокий", h3: 20.007135, accidents: 18.402073, fatal: 20.615385 },
  { label: "Очень высокий", h3: 20.000563, accidents: 74.667568, fatal: 69.230769 },
];

const LOCAL_MORAN = [
  { label: "HH", value: 1894, color: "#636efa" },
  { label: "LH", value: 693, color: "#ef553b" },
  { label: "LL", value: 98, color: "#00cc96" },
];

const SPATIAL_FOLDS = [
  { fold: "1", rows: 13.072061, accidents: 0.682359 },
  { fold: "2", rows: 8.88106, accidents: 3.921998 },
  { fold: "3", rows: 22.390581, accidents: 5.458871 },
  { fold: "4", rows: 28.252495, accidents: 44.525479 },
  { fold: "5", rows: 27.403803, accidents: 45.411293 },
];

function ReportCard({ title, children, className = "" }) {
  return <section className={`statistics-report-card ${className}`.trim()}>{title ? <h3>{title}</h3> : null}{children}</section>;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return <div className="statistics-report-chart-tooltip"><strong>{label}</strong>{payload.map((item) => <div key={item.dataKey}><span style={{ background: item.color }} />{item.name}: {Number(item.value).toFixed(4)}</div>)}</div>;
}

function InfrastructureForest() {
  const data = INFRASTRUCTURE_EFFECTS.map((row) => ({
    ...row,
    error: [row.ratio - row.ciLow, row.ciHigh - row.ratio],
  }));
  return <ResponsiveContainer width="100%" height={510}><ScatterChart margin={{ top: 18, right: 26, bottom: 16, left: 12 }}>
    <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" horizontal={false} />
    <XAxis type="number" dataKey="ratio" domain={[0.7, 2.05]} tick={{ fontSize: 10 }} label={{ value: "IRR", position: "insideBottom", offset: -5, fontSize: 11 }} />
    <YAxis type="category" dataKey="label" width={205} tick={{ fontSize: 10 }} />
    <Tooltip content={<ChartTooltip />} />
    <ReferenceLine x={1} stroke="#243b5a" strokeDasharray="6 5" />
    <Scatter data={data} name="IRR" fill="#636efa"><ErrorBar dataKey="error" width={5} strokeWidth={1.4} stroke="#636efa" direction="x" /></Scatter>
  </ScatterChart></ResponsiveContainer>;
}

function ValidationChart({ data }) {
  return <ResponsiveContainer width="100%" height={340}><BarChart data={data} margin={{ top: 20, right: 18, bottom: 10, left: 2 }}>
    <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
    <XAxis dataKey="metric" tick={{ fontSize: 10 }} interval={0} />
    <YAxis tick={{ fontSize: 10 }} />
    <Tooltip content={<ChartTooltip />} />
    <Legend verticalAlign="top" height={28} />
    <Bar dataKey="logistic" name="L2_logistic_full_primary" fill="#636efa" />
    <Bar dataKey="nb2" name="NB2_negative_binomial_full_primary" fill="#ef553b" />
  </BarChart></ResponsiveContainer>;
}

export default function FrequencySpatialReportSection() {
  return <div className="statistics-frequency-spatial-report">
    <header className="statistics-frequency-hero">
      <h2>Модели пространственной частоты и статистика H3</h2>
      <p>Вкладка показывает, насколько хорошо инфраструктурные признаки объясняют годовую частоту ДТП, как модели ведут себя во временной и пространственной валидации и где остаётся пространственная структура после моделирования.</p>
    </header>

    <div className="statistics-frequency-summary-grid">
      <article><span>Основная count-модель</span><strong>NB2</strong><small>Negative Binomial 2</small></article>
      <article><span>L2 ROC AUC</span><strong>0,8659</strong><small>Holdout 2024</small></article>
      <article><span>L2 PR AUC</span><strong>0,3112</strong><small>Holdout 2024</small></article>
      <article><span>NB2 NLL</span><strong>0,2260</strong><small>Меньше — лучше</small></article>
      <article><span>Pearson residual Moran</span><strong>0,0538</strong><small>pseudo p = 0,01</small></article>
    </div>

    <div className="statistics-report-section-heading"><h3>Качество моделей на 2024 году</h3></div>
    <div className="statistics-report-chart-grid">
      <ReportCard title="Count-модели на holdout 2024"><ResponsiveContainer width="100%" height={350}><BarChart data={COUNT_HOLDOUT} margin={{ top: 20, right: 18, bottom: 60, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="model" tick={{ fontSize: 9, angle: -18, textAnchor: "end" }} /><YAxis tick={{ fontSize: 10 }} label={{ value: "Меньше — лучше", angle: -90, position: "insideLeft", fontSize: 11 }} /><Tooltip content={<ChartTooltip />} /><Legend verticalAlign="top" height={28} /><Bar dataKey="nll" name="Count NLL" fill="#636efa" /><Bar dataKey="poissonDeviance" name="Poisson deviance" fill="#ef553b" />
      </BarChart></ResponsiveContainer></ReportCard>
      <ReportCard title="Бинарные модели на holdout 2024"><ResponsiveContainer width="100%" height={350}><ComposedChart data={BINARY_HOLDOUT} margin={{ top: 20, right: 40, bottom: 60, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="model" tick={{ fontSize: 9, angle: -18, textAnchor: "end" }} /><YAxis yAxisId="auc" domain={[0, 0.9]} tick={{ fontSize: 10 }} label={{ value: "AUC", angle: -90, position: "insideLeft", fontSize: 11 }} /><YAxis yAxisId="loss" orientation="right" domain={[0.18, 0.185]} tick={{ fontSize: 10 }} label={{ value: "Log loss", angle: 90, position: "insideRight", fontSize: 11 }} /><Tooltip content={<ChartTooltip />} /><Legend verticalAlign="top" height={28} /><Bar yAxisId="auc" dataKey="rocAuc" name="ROC AUC" fill="#636efa" /><Bar yAxisId="auc" dataKey="prAuc" name="PR AUC" fill="#ef553b" /><Line yAxisId="loss" dataKey="logLoss" name="Log loss" stroke="#00cc96" dot={{ r: 3 }} />
      </ComposedChart></ResponsiveContainer></ReportCard>
    </div>

    <div className="statistics-report-section-heading"><h3>Проверка переносимости</h3></div>
    <div className="statistics-report-chart-grid">
      <ReportCard title="Средние метрики rolling temporal validation"><ValidationChart data={TEMPORAL_VALIDATION} /></ReportCard>
      <ReportCard title="Средние метрики пространственной block CV"><ValidationChart data={SPATIAL_VALIDATION} /></ReportCard>
    </div>

    <div className="statistics-report-section-heading"><h3>Инфраструктурные ассоциации и пространственные остатки</h3></div>
    <div className="statistics-report-chart-grid">
      <ReportCard title="Инфраструктурные эффекты основной NB2-модели"><InfrastructureForest /></ReportCard>
      <ReportCard title="Глобальная пространственная автокорреляция"><ResponsiveContainer width="100%" height={420}><BarChart data={MORAN} margin={{ top: 20, right: 18, bottom: 70, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="label" tick={{ fontSize: 9, angle: -25, textAnchor: "end" }} /><YAxis tick={{ fontSize: 10 }} label={{ value: "Moran's I", angle: -90, position: "insideLeft", fontSize: 11 }} /><Tooltip content={<ChartTooltip />} /><Bar dataKey="moran" name="Moran's I" fill="#636efa" />
      </BarChart></ResponsiveContainer><p className="statistics-report-card-note">После модели сохраняется слабая положительная глобальная автокорреляция остатков. Это сигнал о неучтённых пространственных факторах, а не доказательство непригодности модели.</p></ReportCard>
    </div>

    <div className="statistics-report-section-heading"><h3>Концентрация риска и локальные кластеры</h3></div>
    <div className="statistics-report-chart-grid">
      <ReportCard title="Концентрация фактических событий по классам риска"><ResponsiveContainer width="100%" height={360}><BarChart data={RISK_CONCENTRATION} margin={{ top: 20, right: 18, bottom: 40, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="label" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 10 }} label={{ value: "%", angle: -90, position: "insideLeft", fontSize: 11 }} /><Tooltip content={<ChartTooltip />} /><Legend verticalAlign="top" height={28} /><Bar dataKey="h3" name="Доля H3, %" fill="#636efa" /><Bar dataKey="accidents" name="Доля ДТП, %" fill="#ef553b" /><Bar dataKey="fatal" name="Доля смертельных ДТП, %" fill="#00cc96" />
      </BarChart></ResponsiveContainer></ReportCard>
      <ReportCard title="Структура значимых Local Moran-кластеров"><ResponsiveContainer width="100%" height={360}><PieChart><Tooltip content={<ChartTooltip />} /><Legend verticalAlign="top" height={28} /><Pie data={LOCAL_MORAN} dataKey="value" nameKey="label" cx="50%" cy="55%" innerRadius={74} outerRadius={125} label={({ name, value, percent }) => `${name}\n${value}\n${(percent * 100).toFixed(1)}%`}>{LOCAL_MORAN.map((row) => <Cell key={row.label} fill={row.color} />)}</Pie></PieChart></ResponsiveContainer></ReportCard>
    </div>

    <div className="statistics-report-section-heading"><h3>Особенность пространственной валидации</h3></div>
    <div className="statistics-report-chart-grid">
      <ReportCard title="Неравномерность пространственных folds"><ResponsiveContainer width="100%" height={340}><BarChart data={SPATIAL_FOLDS} margin={{ top: 20, right: 18, bottom: 28, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="fold" tick={{ fontSize: 10 }} label={{ value: "Fold", position: "insideBottom", offset: -6, fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} label={{ value: "%", angle: -90, position: "insideLeft", fontSize: 11 }} /><Tooltip content={<ChartTooltip />} /><Legend verticalAlign="top" height={28} /><Bar dataKey="rows" name="Доля строк, %" fill="#636efa" /><Bar dataKey="accidents" name="Доля ДТП, %" fill="#ef553b" />
      </BarChart></ResponsiveContainer></ReportCard>
      <ReportCard title="Почему folds различаются"><p>Пространственные блоки существенно различаются по числу ДТП: два folds содержат почти 90% событий. Поэтому spatial CV сложнее обычного случайного разбиения и даёт более консервативную оценку переносимости модели.</p><p>Модель P3 с sensitivity-признаками показала катастрофическую нестабильность в одном пространственном fold и исключена из основных выводов.</p></ReportCard>
    </div>
  </div>;
}
